import * as util from "./util.js";
const Class = util.Class;
const set = util.set;
const sin = Math.sin;
const cos = Math.cos;
const abs = Math.abs;
const log = Math.log;
const asin = Math.asin;
const exp = Math.exp;
const acos = Math.acos;
const fract = Math.fract;
const sign = Math.sign;
const tent = Math.tent;
const atan2 = Math.atan2;
const atan = Math.atan;
const pow = Math.pow;
const sqrt = Math.sqrt;
const floor = Math.floor;
const ceil = Math.ceil;
const min = Math.min;
const max = Math.max;
const PI = Math.PI;
const E = 2.718281828459045;
let M_SQRT2 = Math.sqrt(2.0);
let FLT_EPSILON = 2.22e-16;
const DOT_NORM_SNAP_LIMIT = 0.00000000001;
const basic_funcs: Record<string, [string[], string]> = {
  zero: [[], "0.0;"],
  negate: [[], "-this[X];"],
  combine: [["b", "u", "v"], "this[X]*u + this[X]*v;"],
  interp: [["b", "t"], "this[X] + (b[X] - this[X])*t;"],
  add: [["b"], "this[X] + b[X];"],
  addFac: [["b", "F"], "this[X] + b[X]*F;"],
  fract: [[], "Math.fract(this[X]);"],
  sub: [["b"], "this[X] - b[X];"],
  mul: [["b"], "this[X] * b[X];"],
  div: [["b"], "this[X] / b[X];"],
  mulScalar: [["b"], "this[X] * b;"],
  divScalar: [["b"], "this[X] / b;"],
  addScalar: [["b"], "this[X] + b;"],
  subScalar: [["b"], "this[X] - b;"],
  ceil: [[], "Math.ceil(this[X])"],
  floor: [[], "Math.floor(this[X])"],
  abs: [[], "Math.abs(this[X])"],
  min: [[], "Math.min(this[X])"],
  max: [[], "Math.max(this[X])"],
  clamp: [["MIN", "MAX"], "min(max(this[X], MAX), MIN)"],
};
const bounded_acos = function bounded_acos(fac: number) {
  if (fac <= -1.0) return Math.pi;
  else if (fac >= 1.0) return 0.0;
  else return Math.acos(fac);
};
const saasin = function saasin(fac: number) {
  if (fac <= -1.0) return -Math.pi / 2.0;
  else if (fac >= 1.0) return Math.pi / 2.0;
  else return Math.asin(fac);
};
const HasCSSMatrix = false;
const HasCSSMatrixCopy = false;
class internal_matrix {
  m11 = 0.0;
  m12 = 0.0;
  m13 = 0.0;
  m14 = 0.0;
  m21 = 0.0;
  m22 = 0.0;
  m23 = 0.0;
  m24 = 0.0;
  m31 = 0.0;
  m32 = 0.0;
  m33 = 0.0;
  m34 = 0.0;
  m41 = 0.0;
  m42 = 0.0;
  m43 = 0.0;
  m44 = 0.0;
  // only present on the (dead) WebKitCSSMatrix/CSS path; declared so the
  // guarded HasCSSMatrix(Copy) branches type-check (always-false at runtime).
  declare copy: (array: Float32Array) => void;
  declare inverse: () => internal_matrix;
  declare multiply: (mat: internal_matrix) => internal_matrix;
  declare translate: (x: number, y: number, z: number) => internal_matrix;
  declare scale: (x: number, y: number, z: number) => internal_matrix;
  declare rotateAxisAngle: (x: number, y: number, z: number, angle: number) => internal_matrix;
}
class Matrix4 {
  $matrix: internal_matrix;
  isPersp: boolean;
  static setUniformArray: number[];
  static setUniformWebGLArray: Float32Array;

  constructor(m?: number[] | Matrix4) {
    // HasCSSMatrix is always false; the WebKitCSSMatrix branch is dead.
    this.$matrix = new internal_matrix();
    this.isPersp = false;
    if (typeof m == "object") {
      if ("length" in m && m.length >= 16) {
        this.load(m);
        return;
      } else if (m instanceof Matrix4) {
        this.load(m);
        return;
      }
    }
    this.makeIdentity();
  }

  clone() {
    return new Matrix4(this);
  }

  load(m?: number[] | Matrix4): this {
    if (arguments.length == 1 && typeof arguments[0] == "object") {
      let matrix;
      if (arguments[0] instanceof Matrix4) {
        matrix = arguments[0].$matrix;
        this.isPersp = arguments[0].isPersp;
        this.$matrix.m11 = matrix.m11;
        this.$matrix.m12 = matrix.m12;
        this.$matrix.m13 = matrix.m13;
        this.$matrix.m14 = matrix.m14;
        this.$matrix.m21 = matrix.m21;
        this.$matrix.m22 = matrix.m22;
        this.$matrix.m23 = matrix.m23;
        this.$matrix.m24 = matrix.m24;
        this.$matrix.m31 = matrix.m31;
        this.$matrix.m32 = matrix.m32;
        this.$matrix.m33 = matrix.m33;
        this.$matrix.m34 = matrix.m34;
        this.$matrix.m41 = matrix.m41;
        this.$matrix.m42 = matrix.m42;
        this.$matrix.m43 = matrix.m43;
        this.$matrix.m44 = matrix.m44;
        return this;
      } else matrix = arguments[0];
      if ("length" in matrix && matrix.length >= 16) {
        this.$matrix.m11 = matrix[0];
        this.$matrix.m12 = matrix[1];
        this.$matrix.m13 = matrix[2];
        this.$matrix.m14 = matrix[3];
        this.$matrix.m21 = matrix[4];
        this.$matrix.m22 = matrix[5];
        this.$matrix.m23 = matrix[6];
        this.$matrix.m24 = matrix[7];
        this.$matrix.m31 = matrix[8];
        this.$matrix.m32 = matrix[9];
        this.$matrix.m33 = matrix[10];
        this.$matrix.m34 = matrix[11];
        this.$matrix.m41 = matrix[12];
        this.$matrix.m42 = matrix[13];
        this.$matrix.m43 = matrix[14];
        this.$matrix.m44 = matrix[15];
        return this;
      }
    }

    this.makeIdentity();

    return this;
  }

  toJSON() {
    return { isPersp: this.isPersp, items: this.getAsArray() };
  }

  static fromJSON(json: { items: number[]; isPersp: boolean }) {
    const mat = new Matrix4();
    mat.load(json.items);
    mat.isPersp = json.isPersp;
    return mat;
  }

  getAsArray() {
    return [
      this.$matrix.m11,
      this.$matrix.m12,
      this.$matrix.m13,
      this.$matrix.m14,
      this.$matrix.m21,
      this.$matrix.m22,
      this.$matrix.m23,
      this.$matrix.m24,
      this.$matrix.m31,
      this.$matrix.m32,
      this.$matrix.m33,
      this.$matrix.m34,
      this.$matrix.m41,
      this.$matrix.m42,
      this.$matrix.m43,
      this.$matrix.m44,
    ];
  }

  getAsFloat32Array() {
    if (HasCSSMatrixCopy) {
      const array = new Float32Array(16);
      this.$matrix.copy(array);
      return array;
    }
    return new Float32Array(this.getAsArray());
  }

  setUniform(ctx: WebGLRenderingContext, loc: WebGLUniformLocation | null, transpose: boolean) {
    if (Matrix4.setUniformArray == undefined) {
      Matrix4.setUniformWebGLArray = new Float32Array(16);
      Matrix4.setUniformArray = new Array(16);
    }
    if (HasCSSMatrixCopy) this.$matrix.copy(Matrix4.setUniformWebGLArray);
    else {
      Matrix4.setUniformArray[0] = this.$matrix.m11;
      Matrix4.setUniformArray[1] = this.$matrix.m12;
      Matrix4.setUniformArray[2] = this.$matrix.m13;
      Matrix4.setUniformArray[3] = this.$matrix.m14;
      Matrix4.setUniformArray[4] = this.$matrix.m21;
      Matrix4.setUniformArray[5] = this.$matrix.m22;
      Matrix4.setUniformArray[6] = this.$matrix.m23;
      Matrix4.setUniformArray[7] = this.$matrix.m24;
      Matrix4.setUniformArray[8] = this.$matrix.m31;
      Matrix4.setUniformArray[9] = this.$matrix.m32;
      Matrix4.setUniformArray[10] = this.$matrix.m33;
      Matrix4.setUniformArray[11] = this.$matrix.m34;
      Matrix4.setUniformArray[12] = this.$matrix.m41;
      Matrix4.setUniformArray[13] = this.$matrix.m42;
      Matrix4.setUniformArray[14] = this.$matrix.m43;
      Matrix4.setUniformArray[15] = this.$matrix.m44;
      Matrix4.setUniformWebGLArray.set(Matrix4.setUniformArray);
    }
    ctx.uniformMatrix4fv(loc, transpose, Matrix4.setUniformWebGLArray);
  }

  makeIdentity() {
    this.$matrix.m11 = 1;
    this.$matrix.m12 = 0;
    this.$matrix.m13 = 0;
    this.$matrix.m14 = 0;
    this.$matrix.m21 = 0;
    this.$matrix.m22 = 1;
    this.$matrix.m23 = 0;
    this.$matrix.m24 = 0;
    this.$matrix.m31 = 0;
    this.$matrix.m32 = 0;
    this.$matrix.m33 = 1;
    this.$matrix.m34 = 0;
    this.$matrix.m41 = 0;
    this.$matrix.m42 = 0;
    this.$matrix.m43 = 0;
    this.$matrix.m44 = 1;
  }

  transpose() {
    let tmp = this.$matrix.m12;
    this.$matrix.m12 = this.$matrix.m21;
    this.$matrix.m21 = tmp;
    tmp = this.$matrix.m13;
    this.$matrix.m13 = this.$matrix.m31;
    this.$matrix.m31 = tmp;
    tmp = this.$matrix.m14;
    this.$matrix.m14 = this.$matrix.m41;
    this.$matrix.m41 = tmp;
    tmp = this.$matrix.m23;
    this.$matrix.m23 = this.$matrix.m32;
    this.$matrix.m32 = tmp;
    tmp = this.$matrix.m24;
    this.$matrix.m24 = this.$matrix.m42;
    this.$matrix.m42 = tmp;
    tmp = this.$matrix.m34;
    this.$matrix.m34 = this.$matrix.m43;
    this.$matrix.m43 = tmp;
  }

  invert() {
    if (HasCSSMatrix) {
      this.$matrix = this.$matrix.inverse();
      return;
    }
    const det = this._determinant4x4();
    if (Math.abs(det) < 1e-8) return null;
    this._makeAdjoint();
    this.$matrix.m11 /= det;
    this.$matrix.m12 /= det;
    this.$matrix.m13 /= det;
    this.$matrix.m14 /= det;
    this.$matrix.m21 /= det;
    this.$matrix.m22 /= det;
    this.$matrix.m23 /= det;
    this.$matrix.m24 /= det;
    this.$matrix.m31 /= det;
    this.$matrix.m32 /= det;
    this.$matrix.m33 /= det;
    this.$matrix.m34 /= det;
    this.$matrix.m41 /= det;
    this.$matrix.m42 /= det;
    this.$matrix.m43 /= det;
    this.$matrix.m44 /= det;
  }

  translate(x: number, y: number, z: number) {
    if (typeof x == "object" && "length" in x) {
      const t = x;
      x = t[0];
      y = t[1];
      z = t[2];
    } else {
      if (x == undefined) x = 0;
      if (y == undefined) y = 0;
      if (z == undefined) z = 0;
    }
    if (HasCSSMatrix) {
      this.$matrix = this.$matrix.translate(x, y, z);
      return;
    }
    const matrix = new Matrix4();
    matrix.$matrix.m41 = x;
    matrix.$matrix.m42 = y;
    matrix.$matrix.m43 = z;
    this.multiply(matrix);
  }

  scale(x: number, y: number, z: number) {
    if (typeof x == "object" && "length" in x) {
      const t = x;
      x = t[0];
      y = t[1];
      z = t[2];
    } else {
      if (x == undefined) x = 1;
      if (z == undefined) {
        if (y == undefined) {
          y = x;
          z = x;
        } else z = 1;
      } else if (y == undefined) y = x;
    }
    if (HasCSSMatrix) {
      this.$matrix = this.$matrix.scale(x, y, z);
      return;
    }
    const matrix = new Matrix4();
    matrix.$matrix.m11 = x;
    matrix.$matrix.m22 = y;
    matrix.$matrix.m33 = z;
    this.multiply(matrix);
  }

  euler_rotate(x: number, y: number, z: number, order?: string) {
    if (order == undefined) order = "xyz";
    else order = order.toLowerCase();

    const xmat = new Matrix4();
    let m = xmat.$matrix;
    let c = Math.cos(x);
    let s = Math.sin(x);

    m.m22 = c;
    m.m23 = s;
    m.m32 = -s;
    m.m33 = c;

    const ymat = new Matrix4();
    c = Math.cos(y);
    s = Math.sin(y);
    m = ymat.$matrix;

    m.m11 = c;
    m.m13 = s;
    m.m31 = -s;
    m.m33 = c;

    ymat.multiply(xmat);

    const zmat = new Matrix4();
    c = Math.cos(z);
    s = Math.sin(z);
    m = zmat.$matrix;

    m.m11 = c;
    m.m12 = -s;
    m.m21 = s;
    m.m22 = c;

    zmat.multiply(ymat);

    //console.log(""+ymat);
    this.preMultiply(zmat);
  }

  toString() {
    let s = "";
    const m = this.$matrix;

    function dec(d: number) {
      let ret = d.toFixed(3);

      if (ret[0] != "-")
        //make room for negative signs
        ret = " " + ret;
      return ret;
    }

    s = dec(m.m11) + ", " + dec(m.m12) + ", " + dec(m.m13) + ", " + dec(m.m14) + "\n";
    s += dec(m.m21) + ", " + dec(m.m22) + ", " + dec(m.m23) + ", " + dec(m.m24) + "\n";
    s += dec(m.m31) + ", " + dec(m.m32) + ", " + dec(m.m33) + ", " + dec(m.m34) + "\n";
    s += dec(m.m41) + ", " + dec(m.m42) + ", " + dec(m.m43) + ", " + dec(m.m44) + "\n";

    return s;
  }

  rotate(angle: number, x: number, y: number, z: number) {
    if (typeof x == "object" && "length" in x) {
      const t = x;
      x = t[0];
      y = t[1];
      z = t[2];
    } else {
      if (arguments.length == 1) {
        x = 0;
        y = 0;
        z = 1;
      } else if (arguments.length == 3) {
        this.rotate(angle, 1, 0, 0);
        this.rotate(x, 0, 1, 0);
        this.rotate(y, 0, 0, 1);
        return;
      }
    }
    if (HasCSSMatrix) {
      this.$matrix = this.$matrix.rotateAxisAngle(x, y, z, angle);
      return;
    }
    angle /= 2;
    const sinA = Math.sin(angle);
    const cosA = Math.cos(angle);
    const sinA2 = sinA * sinA;
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len == 0) {
      x = 0;
      y = 0;
      z = 1;
    } else if (len != 1) {
      x /= len;
      y /= len;
      z /= len;
    }
    const mat = new Matrix4();
    if (x == 1 && y == 0 && z == 0) {
      mat.$matrix.m11 = 1;
      mat.$matrix.m12 = 0;
      mat.$matrix.m13 = 0;
      mat.$matrix.m21 = 0;
      mat.$matrix.m22 = 1 - 2 * sinA2;
      mat.$matrix.m23 = 2 * sinA * cosA;
      mat.$matrix.m31 = 0;
      mat.$matrix.m32 = -2 * sinA * cosA;
      mat.$matrix.m33 = 1 - 2 * sinA2;
      mat.$matrix.m14 = mat.$matrix.m24 = mat.$matrix.m34 = 0;
      mat.$matrix.m41 = mat.$matrix.m42 = mat.$matrix.m43 = 0;
      mat.$matrix.m44 = 1;
    } else if (x == 0 && y == 1 && z == 0) {
      mat.$matrix.m11 = 1 - 2 * sinA2;
      mat.$matrix.m12 = 0;
      mat.$matrix.m13 = -2 * sinA * cosA;
      mat.$matrix.m21 = 0;
      mat.$matrix.m22 = 1;
      mat.$matrix.m23 = 0;
      mat.$matrix.m31 = 2 * sinA * cosA;
      mat.$matrix.m32 = 0;
      mat.$matrix.m33 = 1 - 2 * sinA2;
      mat.$matrix.m14 = mat.$matrix.m24 = mat.$matrix.m34 = 0;
      mat.$matrix.m41 = mat.$matrix.m42 = mat.$matrix.m43 = 0;
      mat.$matrix.m44 = 1;
    } else if (x == 0 && y == 0 && z == 1) {
      mat.$matrix.m11 = 1 - 2 * sinA2;
      mat.$matrix.m12 = 2 * sinA * cosA;
      mat.$matrix.m13 = 0;
      mat.$matrix.m21 = -2 * sinA * cosA;
      mat.$matrix.m22 = 1 - 2 * sinA2;
      mat.$matrix.m23 = 0;
      mat.$matrix.m31 = 0;
      mat.$matrix.m32 = 0;
      mat.$matrix.m33 = 1;
      mat.$matrix.m14 = mat.$matrix.m24 = mat.$matrix.m34 = 0;
      mat.$matrix.m41 = mat.$matrix.m42 = mat.$matrix.m43 = 0;
      mat.$matrix.m44 = 1;
    } else {
      const x2 = x * x;
      const y2 = y * y;
      const z2 = z * z;
      mat.$matrix.m11 = 1 - 2 * (y2 + z2) * sinA2;
      mat.$matrix.m12 = 2 * (x * y * sinA2 + z * sinA * cosA);
      mat.$matrix.m13 = 2 * (x * z * sinA2 - y * sinA * cosA);
      mat.$matrix.m21 = 2 * (y * x * sinA2 - z * sinA * cosA);
      mat.$matrix.m22 = 1 - 2 * (z2 + x2) * sinA2;
      mat.$matrix.m23 = 2 * (y * z * sinA2 + x * sinA * cosA);
      mat.$matrix.m31 = 2 * (z * x * sinA2 + y * sinA * cosA);
      mat.$matrix.m32 = 2 * (z * y * sinA2 - x * sinA * cosA);
      mat.$matrix.m33 = 1 - 2 * (x2 + y2) * sinA2;
      mat.$matrix.m14 = mat.$matrix.m24 = mat.$matrix.m34 = 0;
      mat.$matrix.m41 = mat.$matrix.m42 = mat.$matrix.m43 = 0;
      mat.$matrix.m44 = 1;
    }
    this.multiply(mat);
  }

  preMultiply(mat: Matrix4) {
    const tmp = new Matrix4();

    tmp.multiply(this);
    tmp.multiply(mat);

    this.load(tmp);
  }

  multiply(mat: Matrix4) {
    if (HasCSSMatrix) {
      this.$matrix = this.$matrix.multiply(mat.$matrix);
      return;
    }
    const m11 =
      mat.$matrix.m11 * this.$matrix.m11 +
      mat.$matrix.m12 * this.$matrix.m21 +
      mat.$matrix.m13 * this.$matrix.m31 +
      mat.$matrix.m14 * this.$matrix.m41;
    const m12 =
      mat.$matrix.m11 * this.$matrix.m12 +
      mat.$matrix.m12 * this.$matrix.m22 +
      mat.$matrix.m13 * this.$matrix.m32 +
      mat.$matrix.m14 * this.$matrix.m42;
    const m13 =
      mat.$matrix.m11 * this.$matrix.m13 +
      mat.$matrix.m12 * this.$matrix.m23 +
      mat.$matrix.m13 * this.$matrix.m33 +
      mat.$matrix.m14 * this.$matrix.m43;
    const m14 =
      mat.$matrix.m11 * this.$matrix.m14 +
      mat.$matrix.m12 * this.$matrix.m24 +
      mat.$matrix.m13 * this.$matrix.m34 +
      mat.$matrix.m14 * this.$matrix.m44;
    const m21 =
      mat.$matrix.m21 * this.$matrix.m11 +
      mat.$matrix.m22 * this.$matrix.m21 +
      mat.$matrix.m23 * this.$matrix.m31 +
      mat.$matrix.m24 * this.$matrix.m41;
    const m22 =
      mat.$matrix.m21 * this.$matrix.m12 +
      mat.$matrix.m22 * this.$matrix.m22 +
      mat.$matrix.m23 * this.$matrix.m32 +
      mat.$matrix.m24 * this.$matrix.m42;
    const m23 =
      mat.$matrix.m21 * this.$matrix.m13 +
      mat.$matrix.m22 * this.$matrix.m23 +
      mat.$matrix.m23 * this.$matrix.m33 +
      mat.$matrix.m24 * this.$matrix.m43;
    const m24 =
      mat.$matrix.m21 * this.$matrix.m14 +
      mat.$matrix.m22 * this.$matrix.m24 +
      mat.$matrix.m23 * this.$matrix.m34 +
      mat.$matrix.m24 * this.$matrix.m44;
    const m31 =
      mat.$matrix.m31 * this.$matrix.m11 +
      mat.$matrix.m32 * this.$matrix.m21 +
      mat.$matrix.m33 * this.$matrix.m31 +
      mat.$matrix.m34 * this.$matrix.m41;
    const m32 =
      mat.$matrix.m31 * this.$matrix.m12 +
      mat.$matrix.m32 * this.$matrix.m22 +
      mat.$matrix.m33 * this.$matrix.m32 +
      mat.$matrix.m34 * this.$matrix.m42;
    const m33 =
      mat.$matrix.m31 * this.$matrix.m13 +
      mat.$matrix.m32 * this.$matrix.m23 +
      mat.$matrix.m33 * this.$matrix.m33 +
      mat.$matrix.m34 * this.$matrix.m43;
    const m34 =
      mat.$matrix.m31 * this.$matrix.m14 +
      mat.$matrix.m32 * this.$matrix.m24 +
      mat.$matrix.m33 * this.$matrix.m34 +
      mat.$matrix.m34 * this.$matrix.m44;
    const m41 =
      mat.$matrix.m41 * this.$matrix.m11 +
      mat.$matrix.m42 * this.$matrix.m21 +
      mat.$matrix.m43 * this.$matrix.m31 +
      mat.$matrix.m44 * this.$matrix.m41;
    const m42 =
      mat.$matrix.m41 * this.$matrix.m12 +
      mat.$matrix.m42 * this.$matrix.m22 +
      mat.$matrix.m43 * this.$matrix.m32 +
      mat.$matrix.m44 * this.$matrix.m42;
    const m43 =
      mat.$matrix.m41 * this.$matrix.m13 +
      mat.$matrix.m42 * this.$matrix.m23 +
      mat.$matrix.m43 * this.$matrix.m33 +
      mat.$matrix.m44 * this.$matrix.m43;
    const m44 =
      mat.$matrix.m41 * this.$matrix.m14 +
      mat.$matrix.m42 * this.$matrix.m24 +
      mat.$matrix.m43 * this.$matrix.m34 +
      mat.$matrix.m44 * this.$matrix.m44;
    this.$matrix.m11 = m11;
    this.$matrix.m12 = m12;
    this.$matrix.m13 = m13;
    this.$matrix.m14 = m14;
    this.$matrix.m21 = m21;
    this.$matrix.m22 = m22;
    this.$matrix.m23 = m23;
    this.$matrix.m24 = m24;
    this.$matrix.m31 = m31;
    this.$matrix.m32 = m32;
    this.$matrix.m33 = m33;
    this.$matrix.m34 = m34;
    this.$matrix.m41 = m41;
    this.$matrix.m42 = m42;
    this.$matrix.m43 = m43;
    this.$matrix.m44 = m44;
  }

  divide(divisor: number) {
    this.$matrix.m11 /= divisor;
    this.$matrix.m12 /= divisor;
    this.$matrix.m13 /= divisor;
    this.$matrix.m14 /= divisor;
    this.$matrix.m21 /= divisor;
    this.$matrix.m22 /= divisor;
    this.$matrix.m23 /= divisor;
    this.$matrix.m24 /= divisor;
    this.$matrix.m31 /= divisor;
    this.$matrix.m32 /= divisor;
    this.$matrix.m33 /= divisor;
    this.$matrix.m34 /= divisor;
    this.$matrix.m41 /= divisor;
    this.$matrix.m42 /= divisor;
    this.$matrix.m43 /= divisor;
    this.$matrix.m44 /= divisor;
  }

  ortho(left: number, right: number, bottom: number, top: number, near: number, far: number) {
    const tx = (left + right) / (left - right);
    const ty = (top + bottom) / (top - bottom);
    const tz = (far + near) / (far - near);
    const matrix = new Matrix4();
    matrix.$matrix.m11 = 2 / (left - right);
    matrix.$matrix.m12 = 0;
    matrix.$matrix.m13 = 0;
    matrix.$matrix.m14 = 0;
    matrix.$matrix.m21 = 0;
    matrix.$matrix.m22 = 2 / (top - bottom);
    matrix.$matrix.m23 = 0;
    matrix.$matrix.m24 = 0;
    matrix.$matrix.m31 = 0;
    matrix.$matrix.m32 = 0;
    matrix.$matrix.m33 = -2 / (far - near);
    matrix.$matrix.m34 = 0;
    matrix.$matrix.m41 = tx;
    matrix.$matrix.m42 = ty;
    matrix.$matrix.m43 = tz;
    matrix.$matrix.m44 = 1;
    this.multiply(matrix);
  }

  frustum(left: number, right: number, bottom: number, top: number, near: number, far: number) {
    const matrix = new Matrix4();
    const A = (right + left) / (right - left);
    const B = (top + bottom) / (top - bottom);
    const C = -(far + near) / (far - near);
    const D = -(2 * far * near) / (far - near);
    matrix.$matrix.m11 = (2 * near) / (right - left);
    matrix.$matrix.m12 = 0;
    matrix.$matrix.m13 = 0;
    matrix.$matrix.m14 = 0;
    matrix.$matrix.m21 = 0;
    matrix.$matrix.m22 = (2 * near) / (top - bottom);
    matrix.$matrix.m23 = 0;
    matrix.$matrix.m24 = 0;
    matrix.$matrix.m31 = A;
    matrix.$matrix.m32 = B;
    matrix.$matrix.m33 = C;
    matrix.$matrix.m34 = -1;
    matrix.$matrix.m41 = 0;
    matrix.$matrix.m42 = 0;
    matrix.$matrix.m43 = D;
    matrix.$matrix.m44 = 0;
    this.isPersp = true;
    this.multiply(matrix);
  }

  perspective(fovy: number, aspect: number, zNear: number, zFar: number) {
    const top = Math.tan((fovy * Math.PI) / 360) * zNear;
    const bottom = -top;
    const left = aspect * bottom;
    const right = aspect * top;
    this.frustum(left, right, bottom, top, zNear, zFar);
  }

  lookat(
    eyex: number,
    eyey: number,
    eyez: number,
    centerx: number,
    centery: number,
    centerz: number,
    upx: number,
    upy: number,
    upz: number
  ) {
    if (typeof eyez == "object" && "length" in eyez) {
      let t = eyez as ArrayLike<number>;
      upx = t[0];
      upy = t[1];
      upz = t[2];
      t = eyey as unknown as ArrayLike<number>;
      centerx = t[0];
      centery = t[1];
      centerz = t[2];
      t = eyex as unknown as ArrayLike<number>;
      eyex = t[0];
      eyey = t[1];
      eyez = t[2];
    }
    const matrix = new Matrix4();
    let zx = eyex - centerx;
    let zy = eyey - centery;
    let zz = eyez - centerz;
    let mag = Math.sqrt(zx * zx + zy * zy + zz * zz);
    if (mag) {
      zx /= mag;
      zy /= mag;
      zz /= mag;
    }
    let yx = upx;
    let yy = upy;
    let yz = upz;
    let xx;
    let xy;
    let xz;
    xx = yy * zz - yz * zy;
    xy = -yx * zz + yz * zx;
    xz = yx * zy - yy * zx;
    yx = zy * xz - zz * xy;
    yy = -zx * xz + zz * xx;
    yx = zx * xy - zy * xx;
    mag = Math.sqrt(xx * xx + xy * xy + xz * xz);
    if (mag) {
      xx /= mag;
      xy /= mag;
      xz /= mag;
    }
    mag = Math.sqrt(yx * yx + yy * yy + yz * yz);
    if (mag) {
      yx /= mag;
      yy /= mag;
      yz /= mag;
    }
    matrix.$matrix.m11 = xx;
    matrix.$matrix.m12 = xy;
    matrix.$matrix.m13 = xz;
    matrix.$matrix.m14 = 0;
    matrix.$matrix.m21 = yx;
    matrix.$matrix.m22 = yy;
    matrix.$matrix.m23 = yz;
    matrix.$matrix.m24 = 0;
    matrix.$matrix.m31 = zx;
    matrix.$matrix.m32 = zy;
    matrix.$matrix.m33 = zz;
    matrix.$matrix.m34 = 0;
    matrix.$matrix.m41 = 0;
    matrix.$matrix.m42 = 0;
    matrix.$matrix.m43 = 0;
    matrix.$matrix.m44 = 1;
    matrix.translate(-eyex, -eyey, -eyez);
    this.multiply(matrix);
  }

  decompose(
    _translate: number[],
    _rotate: number[],
    _scale: number[],
    _skew: number[],
    _perspective: number[]
  ) {
    if (this.$matrix.m44 == 0) return false;
    const translate =
      _translate == undefined || !("length" in _translate) ? new Vector3() : _translate;
    const rotate = _rotate == undefined || !("length" in _rotate) ? new Vector3() : _rotate;
    const scale = _scale == undefined || !("length" in _scale) ? new Vector3() : _scale;
    const skew = _skew == undefined || !("length" in _skew) ? new Vector3() : _skew;
    const perspective =
      _perspective == undefined || !("length" in _perspective) ? new Array(4) : _perspective;
    const matrix = new Matrix4(this);
    matrix.divide(matrix.$matrix.m44);
    const perspectiveMatrix = new Matrix4(matrix);
    perspectiveMatrix.$matrix.m14 = 0;
    perspectiveMatrix.$matrix.m24 = 0;
    perspectiveMatrix.$matrix.m34 = 0;
    perspectiveMatrix.$matrix.m44 = 1;
    if (perspectiveMatrix._determinant4x4() == 0) return false;

    if (matrix.$matrix.m14 != 0 || matrix.$matrix.m24 != 0 || matrix.$matrix.m34 != 0) {
      const rightHandSide = [
        matrix.$matrix.m14,
        matrix.$matrix.m24,
        matrix.$matrix.m34,
        matrix.$matrix.m44,
      ];
      const inversePerspectiveMatrix = new Matrix4(perspectiveMatrix);
      inversePerspectiveMatrix.invert();
      const transposedInversePerspectiveMatrix = new Matrix4(inversePerspectiveMatrix);
      transposedInversePerspectiveMatrix.transpose();

      const v4 = new Vector3(rightHandSide);
      v4.multVecMatrix(transposedInversePerspectiveMatrix);

      perspective[0] = v4[0];
      perspective[1] = v4[1];
      perspective[2] = v4[2];
      perspective[3] = v4[3];

      matrix.$matrix.m14 = matrix.$matrix.m24 = matrix.$matrix.m34 = 0;
      matrix.$matrix.m44 = 1;
    } else {
      perspective[0] = perspective[1] = perspective[2] = 0;
      perspective[3] = 1;
    }
    translate[0] = matrix.$matrix.m41;
    matrix.$matrix.m41 = 0;
    translate[1] = matrix.$matrix.m42;
    matrix.$matrix.m42 = 0;
    translate[2] = matrix.$matrix.m43;
    matrix.$matrix.m43 = 0;
    const row0 = new Vector3([matrix.$matrix.m11, matrix.$matrix.m12, matrix.$matrix.m13]);
    const row1 = new Vector3([matrix.$matrix.m21, matrix.$matrix.m22, matrix.$matrix.m23]);
    const row2 = new Vector3([matrix.$matrix.m31, matrix.$matrix.m32, matrix.$matrix.m33]);
    scale[0] = row0.vectorLength();
    row0.divide(scale[0]);
    skew[0] = row0.dot(row1);
    row1.combine(row0, 1.0, -skew[0]);
    scale[1] = row1.vectorLength();
    row1.divide(scale[1]);
    skew[0] /= scale[1];
    skew[1] = row1.dot(row2);
    row2.combine(row0, 1.0, -skew[1]);
    skew[2] = row1.dot(row2);
    row2.combine(row1, 1.0, -skew[2]);
    scale[2] = row2.vectorLength();
    row2.divide(scale[2]);
    skew[1] /= scale[2];
    skew[2] /= scale[2];
    const pdum3 = new Vector3(row1);
    pdum3.cross(row2);
    if (row0.dot(pdum3) < 0) {
      for (let i = 0; i < 3; i++) {
        scale[i] *= -1;
        row0[i] *= -1;
        row1[i] *= -1;
        row2[i] *= -1;
      }
    }
    rotate[1] = Math.asin(-row0[2]);
    if (Math.cos(rotate[1]) != 0) {
      rotate[0] = Math.atan2(row1[2], row2[2]);
      rotate[2] = Math.atan2(row0[1], row0[0]);
    } else {
      rotate[0] = Math.atan2(-row2[0], row1[1]);
      rotate[2] = 0;
    }
    const rad2deg = 180 / Math.PI;
    rotate[0] *= rad2deg;
    rotate[1] *= rad2deg;
    rotate[2] *= rad2deg;
    return true;
  }

  _determinant2x2(a: number, b: number, c: number, d: number) {
    return a * d - b * c;
  }

  _determinant3x3(
    a1: number,
    a2: number,
    a3: number,
    b1: number,
    b2: number,
    b3: number,
    c1: number,
    c2: number,
    c3: number
  ) {
    return (
      a1 * this._determinant2x2(b2, b3, c2, c3) -
      b1 * this._determinant2x2(a2, a3, c2, c3) +
      c1 * this._determinant2x2(a2, a3, b2, b3)
    );
  }

  _determinant4x4() {
    const a1 = this.$matrix.m11;
    const b1 = this.$matrix.m12;
    const c1 = this.$matrix.m13;
    const d1 = this.$matrix.m14;
    const a2 = this.$matrix.m21;
    const b2 = this.$matrix.m22;
    const c2 = this.$matrix.m23;
    const d2 = this.$matrix.m24;
    const a3 = this.$matrix.m31;
    const b3 = this.$matrix.m32;
    const c3 = this.$matrix.m33;
    const d3 = this.$matrix.m34;
    const a4 = this.$matrix.m41;
    const b4 = this.$matrix.m42;
    const c4 = this.$matrix.m43;
    const d4 = this.$matrix.m44;
    return (
      a1 * this._determinant3x3(b2, b3, b4, c2, c3, c4, d2, d3, d4) -
      b1 * this._determinant3x3(a2, a3, a4, c2, c3, c4, d2, d3, d4) +
      c1 * this._determinant3x3(a2, a3, a4, b2, b3, b4, d2, d3, d4) -
      d1 * this._determinant3x3(a2, a3, a4, b2, b3, b4, c2, c3, c4)
    );
  }

  _makeAdjoint() {
    const a1 = this.$matrix.m11;
    const b1 = this.$matrix.m12;
    const c1 = this.$matrix.m13;
    const d1 = this.$matrix.m14;
    const a2 = this.$matrix.m21;
    const b2 = this.$matrix.m22;
    const c2 = this.$matrix.m23;
    const d2 = this.$matrix.m24;
    const a3 = this.$matrix.m31;
    const b3 = this.$matrix.m32;
    const c3 = this.$matrix.m33;
    const d3 = this.$matrix.m34;
    const a4 = this.$matrix.m41;
    const b4 = this.$matrix.m42;
    const c4 = this.$matrix.m43;
    const d4 = this.$matrix.m44;
    this.$matrix.m11 = this._determinant3x3(b2, b3, b4, c2, c3, c4, d2, d3, d4);
    this.$matrix.m21 = -this._determinant3x3(a2, a3, a4, c2, c3, c4, d2, d3, d4);
    this.$matrix.m31 = this._determinant3x3(a2, a3, a4, b2, b3, b4, d2, d3, d4);
    this.$matrix.m41 = -this._determinant3x3(a2, a3, a4, b2, b3, b4, c2, c3, c4);
    this.$matrix.m12 = -this._determinant3x3(b1, b3, b4, c1, c3, c4, d1, d3, d4);
    this.$matrix.m22 = this._determinant3x3(a1, a3, a4, c1, c3, c4, d1, d3, d4);
    this.$matrix.m32 = -this._determinant3x3(a1, a3, a4, b1, b3, b4, d1, d3, d4);
    this.$matrix.m42 = this._determinant3x3(a1, a3, a4, b1, b3, b4, c1, c3, c4);
    this.$matrix.m13 = this._determinant3x3(b1, b2, b4, c1, c2, c4, d1, d2, d4);
    this.$matrix.m23 = -this._determinant3x3(a1, a2, a4, c1, c2, c4, d1, d2, d4);
    this.$matrix.m33 = this._determinant3x3(a1, a2, a4, b1, b2, b4, d1, d2, d4);
    this.$matrix.m43 = -this._determinant3x3(a1, a2, a4, b1, b2, b4, c1, c2, c4);
    this.$matrix.m14 = -this._determinant3x3(b1, b2, b3, c1, c2, c3, d1, d2, d3);
    this.$matrix.m24 = this._determinant3x3(a1, a2, a3, c1, c2, c3, d1, d2, d3);
    this.$matrix.m34 = -this._determinant3x3(a1, a2, a3, b1, b2, b3, d1, d2, d3);
    this.$matrix.m44 = this._determinant3x3(a1, a2, a3, b1, b2, b3, c1, c2, c3);
  }
}
util.registerClass(Matrix4);

function make_norm_safe_dot(cls: typeof BaseVector) {
  const _dot = cls.prototype.dot;

  cls.prototype._dot = _dot;
  cls.prototype.dot = function (b) {
    const ret = _dot.call(this, b);

    if (ret >= 1.0 - DOT_NORM_SNAP_LIMIT && ret <= 1.0 + DOT_NORM_SNAP_LIMIT) return 1.0;
    if (ret >= -1.0 - DOT_NORM_SNAP_LIMIT && ret <= -1.0 + DOT_NORM_SNAP_LIMIT) return -1.0;

    return ret;
  };
}

class BaseVector {
  [i: number]: number;
  length: number;

  // eval-generated onto the prototype by inherit() below; declared so the
  // subclasses and BaseVector's own methods type-check without runtime code.
  declare zero: () => this;
  declare negate: () => this;
  declare combine: (b: ArrayLike<number>, u: number, v: number) => this;
  declare interp: (b: ArrayLike<number>, t: number) => this;
  declare add: (b: ArrayLike<number>) => this;
  declare addFac: (b: ArrayLike<number>, f: number) => this;
  declare fract: () => this;
  // real methods (overridden by the eval'd unrolled versions / subclasses) — a
  // declare-field would clash with those method overrides (dot in Vector2/3/4,
  // sub in Quat).
  sub(b: ArrayLike<number>): this {
    return this;
  }
  dot(b: ArrayLike<number>): number {
    return 0;
  }
  // backup of the original dot installed by make_norm_safe_dot()
  declare _dot: (b: ArrayLike<number>) => number;
  declare mul: (b: ArrayLike<number>) => this;
  declare div: (b: ArrayLike<number>) => this;
  declare mulScalar: (b: number) => this;
  declare divScalar: (b: number) => this;
  // alias used by the (dead) matrix-decompose path
  declare divide: (b: number) => this;
  declare addScalar: (b: number) => this;
  declare subScalar: (b: number) => this;
  declare ceil: () => this;
  declare floor: () => this;
  declare abs: () => this;
  declare min: () => this;
  declare max: () => this;
  declare clamp: (min: number, max: number) => this;
  declare vectorDistance: (b: ArrayLike<number>) => number;
  declare vectorDotDistance: (b: ArrayLike<number>) => number;
  // overridden per-subclass

  constructor() {
    this.length = 0;
    //this.xyzw = this.init_swizzle(4);
    //this.xyz = this.init_swizzle(3);
    //this.xy = this.init_swizzle(2);
  }

  *[Symbol.iterator](): Generator<number> {
    for (let i = 0; i < this.length; i++) yield this[i];
  }

  load(data: ArrayLike<number>): this {
    throw new Error("Implement me!");
  }

  vectorLength(): number {
    return sqrt(this.dot(this));
  }

  normalize(): this {
    const l = this.vectorLength();
    if (l > 0.00000001) {
      this.mulScalar(1.0 / l);
    }

    return this;
  }

  static inherit(cls: typeof BaseVector, vectorsize: number) {
    make_norm_safe_dot(cls);

    var f;
    let vectorDotDistance = "f = function vectorDistance(b) {\n";
    for (let i = 0; i < vectorsize; i++) {
      vectorDotDistance += "var d" + i + " = this[" + i + "]-b[" + i + "];\n";
    }

    vectorDotDistance += "return (";
    for (let i = 0; i < vectorsize; i++) {
      if (i > 0) vectorDotDistance += " + ";
      vectorDotDistance += "d" + i + "*d" + i;
    }
    vectorDotDistance += ");\n";
    vectorDotDistance += "};";
    cls.prototype.vectorDotDistance = eval(vectorDotDistance);

    let vectorDistance = "f = function vectorDistance(b) {\n";
    for (let i = 0; i < vectorsize; i++) {
      vectorDistance += "var d" + i + " = this[" + i + "]-b[" + i + "];\n";
    }

    vectorDistance += "return sqrt(";
    for (let i = 0; i < vectorsize; i++) {
      if (i > 0) vectorDistance += " + ";
      vectorDistance += "d" + i + "*d" + i;
    }
    vectorDistance += ");\n";
    vectorDistance += "};";
    cls.prototype.vectorDistance = eval(vectorDistance);

    for (const k in basic_funcs) {
      const func = basic_funcs[k];
      const args = func[0];
      let line = func[1];
      let code = "f = function " + k + "(";
      for (let i = 0; i < args.length; i++) {
        if (i > 0) code += ", ";

        line = line.replace(args[i], args[i].toLowerCase());
        code += args[i].toLowerCase();
      }
      code += ") {\n";

      for (let i = 0; i < vectorsize; i++) {
        const line2 = line.replace(/X/g, "" + i);
        code += "    this[" + i + "] = " + line2 + "\n";
      }

      code += "return this;";
      code += "}\n";

      //console.log(code);
      f = eval(code);

      cls.prototype[k as any] = f;
      //console.log(k, f);
    }
  }
}
util.registerClass(BaseVector);

class Vector4 extends BaseVector {
  constructor(data?: ArrayLike<number>) {
    super();

    if (arguments.length > 1) {
      throw new Error("unexpected argument");
    }

    this.length = 4;
    this[0] = this[1] = this[2] = this[3] = 0.0;

    if (data != undefined) {
      this.load(data);
    }
  }

  load(data?: ArrayLike<number>): this {
    if (data == undefined) return this;

    this[0] = data[0];
    this[1] = data[1];
    this[2] = data[2];
    this[3] = data[3];

    return this;
  }

  dot(b: ArrayLike<number>): number {
    return this[0] * b[0] + this[1] * b[1] + this[2] * b[2] + this[3] * b[3];
  }

  mulVecQuat(_q: ArrayLike<number>): this {
    const t0 = -this[1] * this[0] - this[2] * this[1] - this[3] * this[2];
    let t1 = this[0] * this[0] + this[2] * this[2] - this[3] * this[1];
    let t2 = this[0] * this[1] + this[3] * this[0] - this[1] * this[2];
    this[2] = this[0] * this[2] + this[1] * this[1] - this[2] * this[0];
    this[0] = t1;
    this[1] = t2;
    t1 = t0 * -this[1] + this[0] * this[0] - this[1] * this[3] + this[2] * this[2];
    t2 = t0 * -this[2] + this[1] * this[0] - this[2] * this[1] + this[0] * this[3];
    this[2] = t0 * -this[3] + this[2] * this[0] - this[0] * this[2] + this[1] * this[1];
    this[0] = t1;
    this[1] = t2;

    return this;
  }

  multVecMatrix(matrix: Matrix4): number {
    const x = this[0];
    const y = this[1];
    const z = this[2];
    const w = this[3];
    this[0] =
      matrix.$matrix.m41 +
      x * matrix.$matrix.m11 +
      y * matrix.$matrix.m21 +
      z * matrix.$matrix.m31 +
      w * matrix.$matrix.m41;
    this[1] =
      matrix.$matrix.m42 +
      x * matrix.$matrix.m12 +
      y * matrix.$matrix.m22 +
      z * matrix.$matrix.m32 +
      w * matrix.$matrix.m42;
    this[2] =
      matrix.$matrix.m43 +
      x * matrix.$matrix.m13 +
      y * matrix.$matrix.m23 +
      z * matrix.$matrix.m33 +
      w * matrix.$matrix.m43;
    this[3] =
      w * matrix.$matrix.m44 +
      x * matrix.$matrix.m14 +
      y * matrix.$matrix.m24 +
      z * matrix.$matrix.m34;
    return w;
  }

  cross(v: ArrayLike<number>): this {
    const x = this[1] * v[2] - this[2] * v[1];
    const y = this[2] * v[0] - this[0] * v[2];
    const z = this[0] * v[1] - this[1] * v[0];

    this[0] = x;
    this[1] = y;
    this[2] = z;

    return this;
  }

  preNormalizedAngle(v2: this): number {
    if (this.dot(v2) < 0.0) {
      const vec = new Vector4();
      vec[0] = -v2[0];
      vec[1] = -v2[1];
      vec[2] = -v2[2];
      vec[3] = -v2[3];
      return Math.pi - 2.0 * saasin(vec.vectorDistance(this) / 2.0);
    } else return 2.0 * saasin(v2.vectorDistance(this) / 2.0);
  }
}
util.registerClass(Vector4);

let _v3nd_n1_normalizedDot: Vector3;

let _v3nd_n2_normalizedDot: Vector3;

let _v3nd4_n1_normalizedDot4: Vector3;

let _v3nd4_n2_normalizedDot4: Vector3;

class Vector3 extends BaseVector {
  constructor(data?: ArrayLike<number>) {
    super();

    if (arguments.length > 1) {
      throw new Error("unexpected argument");
    }

    this.length = 3;
    this[0] = this[1] = this[2] = 0.0;

    if (data != undefined) {
      this.load(data);
    }
  }

  load(data?: ArrayLike<number>): this {
    if (data == undefined) return this;

    this[0] = data[0];
    this[1] = data[1];
    this[2] = data[2];

    return this;
  }

  dot(b: ArrayLike<number>): number {
    return this[0] * b[0] + this[1] * b[1] + this[2] * b[2];
  }

  normalizedDot(v: ArrayLike<number>): number {
    _v3nd_n1_normalizedDot.load(this);
    _v3nd_n2_normalizedDot.load(v);
    _v3nd_n1_normalizedDot.normalize();
    _v3nd_n2_normalizedDot.normalize();
    return _v3nd_n1_normalizedDot.dot(_v3nd_n2_normalizedDot);
  }

  //normalizedDot4
  static normalizedDot4(
    v1: ArrayLike<number>,
    v2: ArrayLike<number>,
    v3: ArrayLike<number>,
    v4: ArrayLike<number>
  ): number {
    _v3nd4_n1_normalizedDot4.load(v2).sub(v1).normalize();
    _v3nd4_n2_normalizedDot4.load(v4).sub(v3).normalize();

    return _v3nd4_n1_normalizedDot4.dot(_v3nd4_n2_normalizedDot4);
  }

  multVecMatrix(matrix: Matrix4, ignore_w?: boolean): number {
    if (ignore_w == undefined) {
      ignore_w = false;
    }
    const x = this[0];
    const y = this[1];
    const z = this[2];
    this[0] =
      matrix.$matrix.m41 + x * matrix.$matrix.m11 + y * matrix.$matrix.m21 + z * matrix.$matrix.m31;
    this[1] =
      matrix.$matrix.m42 + x * matrix.$matrix.m12 + y * matrix.$matrix.m22 + z * matrix.$matrix.m32;
    this[2] =
      matrix.$matrix.m43 + x * matrix.$matrix.m13 + y * matrix.$matrix.m23 + z * matrix.$matrix.m33;
    const w =
      matrix.$matrix.m44 + x * matrix.$matrix.m14 + y * matrix.$matrix.m24 + z * matrix.$matrix.m34;
    if (!ignore_w && w != 1 && w != 0 && matrix.isPersp) {
      this[0] /= w;
      this[1] /= w;
      this[2] /= w;
    }
    return w;
  }

  cross(v: ArrayLike<number>): this {
    const x = this[1] * v[2] - this[2] * v[1];
    const y = this[2] * v[0] - this[0] * v[2];
    const z = this[0] * v[1] - this[1] * v[0];

    this[0] = x;
    this[1] = y;
    this[2] = z;

    return this;
  }

  //axis is optional, 0
  rot2d(A: number, axis?: number): this {
    const x = this[0];
    const y = this[1];

    if (axis == 1) {
      this[0] = x * cos(A) + y * sin(A);
      this[1] = y * cos(A) - x * sin(A);
    } else {
      this[0] = x * cos(A) - y * sin(A);
      this[1] = y * cos(A) + x * sin(A);
    }

    return this;
  }

  preNormalizedAngle(v2: this): number {
    if (this.dot(v2) < 0.0) {
      const vec = new Vector3();
      vec[0] = -v2[0];
      vec[1] = -v2[1];
      vec[2] = -v2[2];
      return Math.pi - 2.0 * saasin(vec.vectorDistance(this) / 2.0);
    } else return 2.0 * saasin(v2.vectorDistance(this) / 2.0);
  }
}
util.registerClass(Vector3);

class Vector2 extends BaseVector {
  constructor(data?: ArrayLike<number>) {
    super();

    if (arguments.length > 1) {
      throw new Error("unexpected argument");
    }

    this.length = 2;
    this[0] = this[1] = this[2] = 0.0;

    if (data != undefined) {
      this.load(data);
    }
  }

  load(data?: ArrayLike<number>): this {
    if (data == undefined) return this;

    this[0] = data[0];
    this[1] = data[1];

    return this;
  }

  //axis is optional, 0
  rot2d(A: number, axis?: number): this {
    const x = this[0];
    const y = this[1];

    if (axis == 1) {
      this[0] = x * cos(A) + y * sin(A);
      this[1] = y * cos(A) - x * sin(A);
    } else {
      this[0] = x * cos(A) - y * sin(A);
      this[1] = y * cos(A) + x * sin(A);
    }

    return this;
  }

  dot(b: ArrayLike<number>): number {
    return this[0] * b[0] + this[1] * b[1] + this[2] * b[2];
  }

  mulVecQuat(q: ArrayLike<number>): this {
    const t0 = -this[1] * this[0] - this[2] * this[1] - this[3] * this[2];
    let t1 = this[0] * this[0] + this[2] * this[2] - this[3] * this[1];
    let t2 = this[0] * this[1] + this[3] * this[0] - this[1] * this[2];
    this[2] = this[0] * this[2] + this[1] * this[1] - this[2] * this[0];
    this[0] = t1;
    this[1] = t2;
    t1 = t0 * -this[1] + this[0] * this[0] - this[1] * this[3] + this[2] * this[2];
    t2 = t0 * -this[2] + this[1] * this[0] - this[2] * this[1] + this[0] * this[3];
    this[2] = t0 * -this[3] + this[2] * this[0] - this[0] * this[2] + this[1] * this[1];
    this[0] = t1;
    this[1] = t2;

    return this;
  }
}
util.registerClass(Vector2);

_v3nd4_n1_normalizedDot4 = new Vector3();
_v3nd4_n2_normalizedDot4 = new Vector3();
_v3nd_n1_normalizedDot = new Vector3();
_v3nd_n2_normalizedDot = new Vector3();

BaseVector.inherit(Vector4, 4);
BaseVector.inherit(Vector3, 3);
BaseVector.inherit(Vector2, 2);

export { bounded_acos, saasin, Matrix4, BaseVector, Vector4, Vector3, Vector2 };
